import { useEffect, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'

import { Stack } from '@factorialco/f0-react/dist/experimental'
import {
  CurrencySelectorCurrencyFragment,
  MileageRateConfigurationFragment
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import BasicDetails from 'modules/expenses/components/expenses/Detail/_newForm/BasicDetails'
import Files from 'modules/expenses/components/expenses/Detail/_newForm/Files'
import AdditionalInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/AdditionalInformation'
import BudgetAndProject from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/BudgetAndProject'
import CostCenterInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/CostCenters'
import ExpenseInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/ExpenseInformation/f0'
import MileageInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/MileageInformation/f0'
import TripInformation from 'modules/expenses/components/expenses/Detail/_newForm/Sections/mileage/TripInformation/f0'
import { CustomizationField } from 'modules/expenses/hooks/useExpensesCustomization'
import { ProjectWorker } from 'modules/expenses/hooks/useProjects'
import { RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import { isMyExpensesSection } from 'modules/expenses/lib/common'
import { Expensable } from 'modules/expenses/types/Expensable'
import { BudgetOption } from 'modules/finance/hooks/useBudgets'

interface F0NewMileageFormProps {
  customizationFields: CustomizationField[]
  expensable?: Expensable
  budgetOptions: BudgetOption[]
  projectWorkers: ProjectWorker[]
  hasProjectsFeature: boolean
  loadingProjectWorkers: boolean
  loadingBudgetOptions: boolean
  canEditExpensesWithPendingStatus: boolean
  registerDropHandler?: (onDrop: (files: File[]) => void) => void
  onEmployeeIdChange: (employeeId: number | undefined) => void
  onEffectiveOnChange: (value: string) => void
  hasReportOnBehalfFeature: boolean
  canCreateExpensesForOthers: boolean
  canSeeEmployeeLegalEntity: boolean
  rates?: MileageRateConfigurationFragment[]
  currencies?: CurrencySelectorCurrencyFragment[]
  scrollTo?: string | undefined
  hasMileageMultipleStops?: boolean
}

const F0NewMileageForm = (props: F0NewMileageFormProps) => {
  const { section } = useOutletContext<RouteContextParams>()
  const {
    expensable,
    budgetOptions,
    projectWorkers,
    hasProjectsFeature,
    loadingProjectWorkers,
    loadingBudgetOptions,
    registerDropHandler,
    onEmployeeIdChange,
    onEffectiveOnChange,
    rates,
    scrollTo,
    canEditExpensesWithPendingStatus
  } = props

  const asReviewer = !isMyExpensesSection(section)

  const { employee } = useCurrentSession()

  // State to track distance calculation loading
  const [isCalculatingDistance, setIsCalculatingDistance] = useState(false)

  const tripInformationRef = useRef<HTMLDivElement>(null)
  const mileageInformationRef = useRef<HTMLDivElement>(null)
  const basicInformationRef = useRef<HTMLDivElement>(null)
  const budgetAndProjectRef = useRef<HTMLDivElement>(null)
  const additionalInformationRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    // The timeout gives a more graceful animation experience
    setTimeout(() => {
      let ref: HTMLDivElement | null = null
      switch (scrollTo) {
        case 'basicInformation':
          ref = basicInformationRef.current
          break
        case 'tripInformation':
          ref = tripInformationRef.current
          break
        case 'mileageInformation':
          ref = mileageInformationRef.current
          break
        case 'budgetAndProject':
          ref = budgetAndProjectRef.current
          break
        case 'additionalInformation':
          ref = additionalInformationRef.current
          break
      }
      if (ref) {
        ref.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 300)
  }, [scrollTo])

  return (
    <Stack
      gap="6"
      style={{
        paddingTop: expensable ? '24px' : '8px',
        paddingBottom: expensable ? '24px' : '8px'
      }}
    >
      <BasicDetails
        expensable={expensable}
        canEditExpensesWithPendingStatus={canEditExpensesWithPendingStatus}
      />
      <div ref={basicInformationRef}>
        <ExpenseInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          onEmployeeIdChange={onEmployeeIdChange}
          isAutomaticExpensable={false}
          documentType={'default'}
          onEffectiveOnChange={onEffectiveOnChange}
        />
      </div>
      <div ref={tripInformationRef}>
        <TripInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          setIsCalculatingDistance={setIsCalculatingDistance}
          hasMileageMultipleStops={props.hasMileageMultipleStops}
        />
      </div>
      <div ref={mileageInformationRef}>
        <MileageInformation
          {...props}
          documentType={'default'}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          rates={rates}
          isCalculatingDistance={isCalculatingDistance}
          setIsCalculatingDistance={setIsCalculatingDistance}
        />
      </div>
      <div ref={budgetAndProjectRef}>
        <BudgetAndProject
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          budgetOptions={budgetOptions}
          projectWorkers={projectWorkers}
          hasProjectsFeature={hasProjectsFeature}
          loadingProjectWorkers={loadingProjectWorkers}
          loadingBudgetOptions={loadingBudgetOptions}
        />
      </div>
      <div>
        <CostCenterInformation
          {...props}
          currentEmployee={employee}
          resourceId={expensable?.id}
          asReviewer={asReviewer}
          resourceType={'expense'}
          documentType={'default'}
        />
      </div>
      <div ref={additionalInformationRef}>
        <AdditionalInformation
          {...props}
          currentEmployee={employee}
          resourceType={'mileage'}
          asReviewer={asReviewer}
          documentType={'default'}
          expensable={expensable}
        />
      </div>
      <Files
        {...props}
        currentEmployee={employee}
        fieldIdentifier="files"
        resourceType={'mileage'}
        asReviewer={asReviewer}
        documentType={'default'}
        expensable={expensable}
        registerDropHandler={registerDropHandler}
        taxAgencyOrganization={null}
      />
    </Stack>
  )
}
export default F0NewMileageForm
